<template>
  <v-card flat class="filtros-obras-autorizadas">
    <v-card-text>
      <v-row align="center" dense>
        <!-- Ejercicio Picker -->
        <v-col cols="12" sm="6" md="2">
          <ejercicio-picker
            v-model="filtrosInternos.ejercicio"
            @input="emitirCambios"
            sinPeriodo
          />
        </v-col>

        <!-- Fuente Financiera -->
        <v-col cols="12" sm="6" md="3">
          <custom-select
            v-model="filtrosInternos.idFuenteFinanciera"
            :items="fuentesFinancieras"
            item-text="nombre"
            item-value="id"
            label="Fuente Financiera"
            clearable
            hide-details
            filled
            @input="emitirCambios"
          >
            <template v-slot:prepend-inner>
              <v-icon small>mdi-bank</v-icon>
            </template>
          </custom-select>
        </v-col>

        <!-- Buscador de Texto con Debounce -->
        <v-col cols="12" sm="12" md="5">
          <custom-text-field
            v-model="searchText"
            label="Buscar por clave, meta u oficio"
            placeholder="Escribe para buscar..."
            outlined
            dense
            clearable
            hide-details
            @input="debouncedSearch"
            @click:clear="limpiarBusqueda"
          >
            <template v-slot:prepend-inner>
              <v-icon small>mdi-magnify</v-icon>
            </template>
            <template v-slot:append>
              <v-progress-circular
                v-if="isSearching"
                indeterminate
                size="20"
                width="2"
                color="primary"
              />
            </template>
          </custom-text-field>
        </v-col>

        <!-- Botón Limpiar Filtros -->
        <v-col cols="12" sm="12" md="2" class="text-right">
          <v-btn
            outlined
            color="grey darken-1"
            small
            @click="limpiarFiltros"
            :disabled="!hayCambios"
          >
            <v-icon small left>mdi-filter-remove</v-icon>
            Limpiar
          </v-btn>
        </v-col>
      </v-row>

      <!-- Chips de Filtros Activos -->
      <v-row v-if="hayCambios" dense class="mt-2">
        <v-col cols="12">
          <div class="d-flex align-center flex-wrap">
            <span class="text-caption grey--text mr-2">Filtros activos:</span>

            <v-chip
              v-if="filtrosInternos.ejercicio !== new Date().getFullYear()"
              small
              close
              color="primary"
              outlined
              class="mr-2 mb-1"
              @click:close="limpiarEjercicio"
            >
              <v-icon small left>mdi-calendar</v-icon>
              {{ filtrosInternos.ejercicio }}
            </v-chip>

            <v-chip
              v-if="filtrosInternos.idFuenteFinanciera"
              small
              close
              color="info"
              outlined
              class="mr-2 mb-1"
              @click:close="limpiarFuenteFinanciera"
            >
              <v-icon small left>mdi-bank</v-icon>
              {{ getNombreFuenteFinanciera }}
            </v-chip>

            <v-chip
              v-if="filtrosInternos.buscarTexto"
              small
              close
              color="secondary"
              outlined
              class="mr-2 mb-1"
              @click:close="limpiarTexto"
            >
              <v-icon small left>mdi-magnify</v-icon>
              "{{ filtrosInternos.buscarTexto }}"
            </v-chip>
          </div>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import EjercicioPicker from '@/components/inputs/EjercicioPicker.vue';
import CustomSelect from '@/components/inputs/Select.vue';
import CustomTextField from '@/components/inputs/sinColumnas/TextField.vue';
import FUENTES_FINANCIERAS from '@/graphql/querys/administracion/catalogos/fuentesfinancieras/fuentesFinancieras.gql';

export default {
  name: 'FiltrosObrasAutorizadas',
  components: {
    EjercicioPicker,
    CustomSelect,
    CustomTextField,
  },
  props: {
    value: {
      type: Object,
      default: () => ({
        ejercicio: new Date().getFullYear(),
        idFuenteFinanciera: null,
        buscarTexto: '',
      }),
    },
    debounceTime: {
      type: Number,
      default: 600,
    },
  },
  data() {
    return {
      filtrosInternos: {
        ejercicio: new Date().getFullYear(),
        idFuenteFinanciera: null,
        buscarTexto: '',
      },
      searchText: '',
      isSearching: false,
      debounceTimer: null,
      fuentesFinancieras: [],
      filtrosIniciales: null,
    };
  },
  apollo: {
    fuentesFinancieras: {
      query: FUENTES_FINANCIERAS,
      variables: {
        activos: true,
      },
      update(data) {
        return data.fuentesFinancieras || [];
      },
    },
  },
  computed: {
    hayCambios() {
      if (!this.filtrosIniciales) return false;

      return (
        this.filtrosInternos.ejercicio !== this.filtrosIniciales.ejercicio
        || this.filtrosInternos.idFuenteFinanciera !== this.filtrosIniciales.idFuenteFinanciera
        || this.filtrosInternos.buscarTexto !== this.filtrosIniciales.buscarTexto
      );
    },
    getNombreFuenteFinanciera() {
      if (!this.filtrosInternos.idFuenteFinanciera) return '';

      const fuente = this.fuentesFinancieras.find(
        (f) => f.id === this.filtrosInternos.idFuenteFinanciera,
      );

      return fuente ? fuente.nombre : '';
    },
  },
  watch: {
    value: {
      handler(newVal) {
        if (newVal) {
          this.filtrosInternos = { ...newVal };
          this.searchText = newVal.buscarTexto || '';
        }
      },
      deep: true,
      immediate: true,
    },
  },
  mounted() {
    // Guardar el estado inicial de los filtros
    this.filtrosIniciales = {
      ejercicio: new Date().getFullYear(),
      idFuenteFinanciera: null,
      buscarTexto: '',
    };
  },
  methods: {
    debouncedSearch(value) {
      // Si el valor es null o undefined (por ejemplo, al limpiar), tratar como string vacío
      const searchValue = value === null || value === undefined ? '' : value;

      this.isSearching = true;

      // Limpiar el timer anterior
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }

      // Crear nuevo timer
      this.debounceTimer = setTimeout(() => {
        this.filtrosInternos.buscarTexto = searchValue.trim();
        this.isSearching = false;
        this.emitirCambios();
      }, this.debounceTime);
    },
    limpiarBusqueda() {
      // Método específico para cuando se limpia el campo con el botón X
      this.searchText = '';
      this.filtrosInternos.buscarTexto = '';
      this.isSearching = false;

      // Limpiar el timer si existe
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }

      this.emitirCambios();
    },
    emitirCambios() {
      this.$emit('input', { ...this.filtrosInternos });
      this.$emit('change', { ...this.filtrosInternos });
    },
    limpiarFiltros() {
      this.filtrosInternos = {
        ejercicio: new Date().getFullYear(),
        idFuenteFinanciera: null,
        buscarTexto: '',
      };
      this.searchText = '';
      this.emitirCambios();
    },
    limpiarEjercicio() {
      this.filtrosInternos.ejercicio = new Date().getFullYear();
      this.emitirCambios();
    },
    limpiarFuenteFinanciera() {
      this.filtrosInternos.idFuenteFinanciera = null;
      this.emitirCambios();
    },
    limpiarTexto() {
      this.filtrosInternos.buscarTexto = '';
      this.searchText = '';
      this.emitirCambios();
    },
  },
  beforeDestroy() {
    // Limpiar el timer al destruir el componente
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  },
};
</script>

<style lang="scss" scoped>
.filtros-obras-autorizadas {
  ::v-deep {
    .v-text-field--outlined {
      fieldset {
        border-color: rgba(0, 0, 0, 0.23);
      }

      &.v-input--is-focused fieldset {
        border-color: var(--v-primary-base);
      }
    }

    .v-select.v-input--dense .v-select__selection--comma {
      margin: 4px 4px 4px 0;
    }
  }
}
</style>
